import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  Backdrop,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { verifyOtp, verifyResetPasswordOtp } from "../../redux/action/otp";
import ResetPasswordModal from "../../modals/ResetPasswordModal";
import { getCarrierByBusinessName } from "../../redux/action/carrier";
import { unwrapResult } from "@reduxjs/toolkit";

const OtpVerification = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { email, isPasswordReset, phoneNumber, moveId } = location.state || {};
  const businessName = localStorage.getItem("businessName");
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [carrierCanonicalId, setCarrierCanonicalId] = useState("");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  useEffect(() => {
    if (businessName) {
      dispatch<any>(getCarrierByBusinessName(businessName))
        .then(unwrapResult)
        .then((res: any) => {
          setCarrierCanonicalId(res.canonical_id);
        })
        .catch((err: any) => {
          console.error("Failed to fetch carrier data:", err);
        });
    }
  }, [businessName]);
  const handleChange = (index: number, value: string) => {
    const newOtp = [...otp];
    if (value === "") {
      newOtp[index] = "";
      setOtp(newOtp);
      if (index > 0) {
        const previousInput = document.getElementById(`otp-input-${index - 1}`);
        if (previousInput) previousInput.focus();
      }
    } else if (/^\d?$/.test(value)) {
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 3) {
        const nextInput = document.getElementById(`otp-input-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleConfirm = async () => {
    const otpCode = otp.join("");
    if (otpCode.length !== 4) {
      setError("Please enter a 4-digit OTP");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      if (isPasswordReset) {
        await dispatch<any>(
          verifyResetPasswordOtp({ email, otp: otpCode })
        ).unwrap();
        setResetPasswordOpen(true);
      } else {
        const currentCarrierCanonicalId = carrierCanonicalId || "";

        const result = await dispatch<any>(
          verifyOtp({
            email,
            otp: otpCode,
            carrierCanonicalId: currentCarrierCanonicalId,
          })
        ).unwrap();
        localStorage.setItem("authToken", result.token);
        localStorage.setItem("user-canonical-id", result.canonicalId);
        const newCanonicalId = result.canonicalId;
        const fromUserDetails =
          localStorage.getItem("fromUserDetails") === "true";
        const moveServiceCanonicalId = localStorage.getItem("canonicalId");

        if (fromUserDetails) {
          navigate(
            `/${businessName}/${carrierCanonicalId}/move-summary/${moveId}`
          );
          localStorage.removeItem("fromUserDetails");
          localStorage.removeItem("canonicalId");
        } else {
          navigate(
            `/${businessName}/${carrierCanonicalId}/consumer-dashboard/${result.canonicalId}`
          );
        }
      }
    } catch (error: any) {
      setError(error || "Failed to verify OTP");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          height: "100%",
          background:
            "linear-gradient(-45deg, #ee7752, #e73c7e, #23d5ab, #23a6d5)",
          backgroundSize: "400% 400%",
          animation: "gradientAnimation 20s ease infinite",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: isMobile ? 1 : 3,
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            maxWidth: 500,
            width: "100%",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 3,
            p: isMobile ? 2 : 3,
            mx: isMobile ? 1 : 3,
            textAlign: "center",
            position: "relative",
            zIndex: 1,
            overflowY: "auto",
            maxHeight: "90vh",
            boxSizing: "border-box",
          }}
        >
          <Typography variant="h4" fontWeight={600} gutterBottom textAlign="center">
            Enter OTP
          </Typography>
          <Typography color="textSecondary" mb={3} textAlign="center">
            We've sent a verification code to your email
          </Typography>

          <Box display="flex" justifyContent="center" gap={2} mb={4}>
            {otp.map((digit, index) => (
              <TextField
                key={index}
                id={`otp-input-${index}`}
                variant="outlined"
                type="text"
                inputProps={{
                  maxLength: 1,
                  style: { textAlign: "center", fontSize: "24px" },
                }}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                sx={{ width: 50, height: 50 }}
              />
            ))}
          </Box>

          {error && (
            <Typography color="error" textAlign="center" mb={2}>
              {error}
            </Typography>
          )}

          <Button
            variant="contained"
            fullWidth
            onClick={handleConfirm}
            disabled={isLoading}
            sx={{ mb: 2 }}
          >
            {isLoading ? <CircularProgress size={24} /> : "Verify OTP"}
          </Button>
        </Box>
      </Box>

      {isLoading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <ResetPasswordModal
        open={resetPasswordOpen}
        onClose={() => setResetPasswordOpen(false)}
        email={email}
        otp={otp.join("")}
      />

      <style>
        {`
          @keyframes gradientAnimation {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }
        `}
      </style>
    </>
  );
};

export default OtpVerification;
